import { Stack, Text, Card } from 'braid-design-system';
import React, { Fragment } from 'react';

import type { ClientListingExpireUpdate } from 'src/hooks/useExpireListing';
import type { ClientListingPostUpdate } from 'src/hooks/usePostListing';
import type { DashboardListingFilter } from 'src/types/dashboard';
import type {
  ClientListingForListingsTab,
  ListingProductForListingsTab,
} from 'src/types/graphql-derived';
import { useOneTimeSort } from 'src/views/Dashboard/useOneTimeSort';

import Row from './Row/Row';

interface FilteredListingsProps {
  filter: DashboardListingFilter;
  listings: ClientListingForListingsTab[];
  listingProducts: ListingProductForListingsTab[];
  onListingExpire: (id: number, listing: ClientListingExpireUpdate) => void;
  onListingPost: (id: number, listing: ClientListingPostUpdate) => void;
}

export const FilteredListings = ({
  filter,
  listings,
  listingProducts,
  onListingExpire,
  onListingPost,
}: FilteredListingsProps) => {
  const sortedListings = useOneTimeSort(listings);
  const filteredListings = sortedListings
    .filter(
      (listing) =>
        !listing.isDeleted && (listing.status === filter || filter === 'all'),
    )
    .map((listing) => (
      <Row
        key={listing.id}
        listing={listing}
        listingProducts={listingProducts}
        onListingExpire={onListingExpire}
        onListingPost={onListingPost}
      />
    ));

  if (filteredListings.length >= 1) {
    return <Fragment>{filteredListings}</Fragment>;
  }

  // to deal with changing the UI word "expired" to "removed"
  let filterListingsTextDisplay = `You have no ${filter.toLowerCase()} ads.`;
  if (filter.toLowerCase() === 'expired') {
    filterListingsTextDisplay = 'You have no removed ads.';
  }

  // The parent enforces that there will always be 1 listing but given this is an
  // array we can't inform TS. In similar scenarios I have seen people advocate for
  // a purpose built data structure that elimates this possibility
  if (listings.length >= 1) {
    return (
      <Stack space="medium" align="center">
        <Card>
          <Text size="large">{filterListingsTextDisplay}</Text>
        </Card>
      </Stack>
    );
  }

  return null;
};
